/**
 * The provenance line under every m³ block (spec §12.2), as translatable
 * segments the panel joins with " · ":
 *   - cut & fill: "Grid 0.25 m · as-built sampled by median · coverage 97 %";
 *   - deviation:  "From deviation grid 0.05 m (mean)".
 *
 * A segment-for-segment twin of the report's
 * `processReport/comparisonSummary.ts::cutFillProvenance` and the deviation
 * section's `provenance.deviation` line, so the panel and the PDF say the
 * same thing. An unknown figure drops its segment rather than printing a dash.
 *
 * `method` params are raw sampling tokens (`min`, `median`, `p05`, …); the
 * caller words them (`compareSurfaces.sampling.<token>`).
 */
import type { TFunction } from 'i18next';

import type { SurfaceDifferenceMeta } from '@/components/SurfaceDifferencePanel';

import type { CompareKind } from './availability';

export interface ProvenanceSegment {
  key: string;
  params: Record<string, string | number>;
}

/**
 * The fields the cut & fill line reads off a result layer's
 * `metadata.surfaceDifference`. `params` is the run's own params block
 * (postprocess copies it), carrying `compareKind`. The sampling methods and
 * the point-cloud flags are copied from the job input by postprocess (ruling
 * R13); a layer made before that has none of them and its line simply has no
 * "sampled by" segment.
 */
export type CutFillProvenanceMeta = Pick<SurfaceDifferenceMeta, 'stats'> & {
  params?: { compareKind?: CompareKind | string | null } | null;
  topPcMethod?: string | null;
  bottomPcMethod?: string | null;
  topIsPointCloud?: boolean | null;
  bottomIsPointCloud?: boolean | null;
};

const finite = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);

/** A side's method, only when that side was a point cloud. Postprocess
 *  already writes `null` for a non-cloud side; an explicit `false` flag is
 *  honoured too, so a TIN is never "sampled by" anything. */
const sideMethod = (method: string | null | undefined, isPointCloud: boolean | null | undefined) =>
  method && isPointCloud !== false ? method : null;

export function cutFillProvenance(meta: CutFillProvenanceMeta): ProvenanceSegment[] {
  const segments: ProvenanceSegment[] = [];
  // The grid the worker actually used — coarsened above 25 M cells.
  const res = finite(meta.stats?.effectiveResolution) ? meta.stats.effectiveResolution : meta.stats?.requestedResolution;
  if (finite(res)) segments.push({ key: 'compareSurfaces.provenance.grid', params: { res } });

  // Design mode names only the as-built side: a point-cloud design is not
  // what the sentence is about. Survey mode names the earlier survey first.
  const top = sideMethod(meta.topPcMethod, meta.topIsPointCloud);
  const bottom = sideMethod(meta.bottomPcMethod, meta.bottomIsPointCloud);
  const survey = meta.params?.compareKind === 'survey';
  const sampled = survey
    ? bottom
      ? { key: 'compareSurfaces.provenance.sampledEarlier', method: bottom }
      : top
        ? { key: 'compareSurfaces.provenance.sampledLater', method: top }
        : null
    : bottom
      ? { key: 'compareSurfaces.provenance.sampledAsBuilt', method: bottom }
      : null;
  if (sampled) segments.push({ key: sampled.key, params: { method: sampled.method } });

  if (finite(meta.stats?.coveragePct))
    segments.push({ key: 'compareSurfaces.provenance.coverage', params: { pct: Math.round(meta.stats.coveragePct) } });
  return segments;
}

/** The deviation volume line, from `metadata.deviationStats.volume`; `null`
 *  when the raster grid is unknown. */
export function deviationProvenance(stats: { rasterResolutionM?: number | null } | null | undefined): ProvenanceSegment | null {
  const res = stats?.rasterResolutionM;
  return finite(res) ? { key: 'compareSurfaces.provenance.deviation', params: { res } } : null;
}

type Translate = (key: string, vars?: Record<string, unknown>) => string;

/**
 * A grid size the way the report's `plain()` prints it — at most three
 * decimals, so a coarsened 0.4123105625617661 m reads 0.412 m — with the
 * reader's decimal separator.
 */
const formatGrid = (res: number, locale: string): string =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 3, useGrouping: false }).format(+res.toFixed(3));

/**
 * The segments as one line, joined with " · ". A `method` param is a raw
 * sampling token and is worded through `compareSurfaces.sampling.<token>`; a
 * token with no wording prints as given, like the report's `samplingName`. A
 * `res` param is a grid size in metres (`formatGrid`).
 *
 * Takes the panel's own `t` and `i18n.language`; the segment keys are built at
 * run time, so `t` is called through the plain-string signature.
 */
export function formatProvenance(
  segments: readonly ProvenanceSegment[],
  translate: TFunction<'project'> | Translate,
  locale: string,
): string {
  const t = translate as Translate;
  const word = (method: string) => {
    const key = `compareSurfaces.sampling.${method}`;
    const name = t(key);
    return name === key ? method : name;
  };
  return segments
    .map((s) => {
      const { method, res } = s.params;
      return t(s.key, {
        ...s.params,
        ...(typeof method === 'string' ? { method: word(method) } : {}),
        ...(typeof res === 'number' ? { res: formatGrid(res, locale) } : {}),
      });
    })
    .join(' · ');
}
